import { useState, useEffect, useRef, useCallback, Component } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, Phone, Upload, History, LogOut,
  Key, Database, MessageCircle, Settings, CreditCard, Mail, BarChart3, FileText,
  Wallet, ShoppingCart, Loader2, CheckCircle2, Activity, RefreshCw, Users, Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiCall, API } from "@/lib/api";
import { SingleTab } from "@/components/tabs/SingleTab";
import { BulkTab } from "@/components/tabs/BulkTab";
import { ApiTab } from "@/components/tabs/ApiTab";
import { EnacomTab } from "@/components/tabs/EnacomTab";
import { UsageTab } from "@/components/tabs/UsageTab";
import { ApiUsageTab } from "@/components/tabs/ApiUsageTab";
import { CompanyUsersTab } from "@/components/tabs/CompanyUsersTab";
import { EmailBulkTab } from "@/components/tabs/EmailBulkTab";
import { NotificationBell } from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";

// ==================== CHANGE PASSWORD DIALOG ====================
const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!current || !next) { setError("Ingresá contraseña actual y nueva"); return; }
    setSubmitting(true);
    const { ok, data } = await apiCall("POST", `${API}/auth/change-password`, {
      current_password: current, new_password: next,
    });
    setSubmitting(false);
    if (ok) {
      toast.success("Contraseña actualizada");
      setCurrent(""); setNext(""); setOpen(false);
    } else {
      setError(data?.detail || "No se pudo cambiar la contraseña");
    }
  };

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)} className="t-text-secondary hover:bg-violet-500/10" title="Cambiar contraseña" data-testid="change-password-btn">
        <Lock className="w-4 h-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-zinc-950 border-white/10" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Lock className="w-5 h-5 text-violet-400" />Cambiar contraseña</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">Ingresá tu contraseña actual y la nueva (mínimo 8 caracteres con al menos 1 número).</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Contraseña actual</label>
              <Input type="password" value={current} onChange={e => setCurrent(e.target.value)} className="bg-zinc-900 border-zinc-800 text-white" autoComplete="current-password" data-testid="cp-current-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Nueva contraseña (≥8 chars + 1 número)</label>
              <Input type="password" value={next} onChange={e => setNext(e.target.value)} className="bg-zinc-900 border-zinc-800 text-white" autoComplete="new-password" data-testid="cp-new-input" />
            </div>
            {error && (
              <div className="p-2 bg-rose-500/15 border border-rose-500/40 rounded text-xs text-rose-600 dark:text-rose-300 font-medium" data-testid="cp-error">{error}</div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="cp-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}Actualizar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const BalanceBar = ({ balance, onBuy }) => {
  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-4 backdrop-blur-sm mb-6" data-testid="balance-bar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-400" />
            <span className="font-semibold text-white text-sm">Saldo</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20">
            <span className="font-bold text-lg text-amber-400" data-testid="balance-amount">${(balance?.balance_ars || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
          </div>
          {balance?.plan_name && balance.plan_name !== "Free" && (
            <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 text-xs">Plan {balance.plan_name}</Badge>
          )}
          {balance?.discount_pct > 0 && (
            <span className="text-xs text-emerald-400">-{balance.discount_pct}% en precios</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">~{balance?.estimated_phone || 0} tel | ~{balance?.estimated_wa || 0} WA</span>
          <Button onClick={onBuy} size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="buy-credits-btn">
            <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />Comprar Saldo
          </Button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [credits, setCredits] = useState(null);
  const [services, setServices] = useState(null);
  const [focusCampaignId, setFocusCampaignId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [plans, setPlans] = useState([]);
  const [buyingPlan, setBuyingPlan] = useState(null);

  const [networkError, setNetworkError] = useState(false);

  const isAdmin = user?.role === "superadmin";

  const fetchBalance = useCallback(() => {
    apiCall("GET", `${API}/my/balance`).then(({ ok, data }) => { if (ok) setBalance(data); }).catch(() => {});
  }, []);

  const loadDashboardData = useCallback(() => {
    setNetworkError(false);
    apiCall("GET", `${API}/version`).then(({ data }) => setAppVersion(data.version || "")).catch(() => {});
    apiCall("GET", `${API}/my/credits`).then(({ ok, data }) => { if (ok) setCredits(data); }).catch(() => {});
    apiCall("GET", `${API}/my/services`).then(({ ok, data }) => {
      if (ok) {
        setServices(data.services);
      } else {
        setNetworkError(true);
      }
    }).catch(() => { setNetworkError(true); });
    fetchBalance();
  }, [fetchBalance]);

  // Handle payment redirect
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Pago aprobado! Tu saldo fue acreditado.");
      fetchBalance();
    } else if (paymentStatus === "failure") {
      toast.error("El pago fue rechazado. Intenta de nuevo.");
    } else if (paymentStatus === "pending") {
      toast.info("Tu pago esta pendiente. Se acreditara cuando se confirme.");
    }
  }, [searchParams, fetchBalance]);

  // Set initial tab based on services
  useEffect(() => {
    if (services && !activeTab) {
      setActiveTab("bulk");
    }
  }, [services, activeTab]);

  useEffect(() => {
    loadDashboardData();
    // Fallback: if services hasn't loaded after 5 seconds, use defaults
    const svcTimeout = setTimeout(() => {
      setServices(prev => prev || { phone_validation: true, email_validation: true, whatsapp_validation: { enabled: false } });
    }, 5000);
    return () => clearTimeout(svcTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenBuy = async () => {
    try {
      const { ok, data } = await apiCall("GET", `${API}/plans`);
      if (ok) setPlans((data.plans || []).filter(p => p.active && p.price > 0));
    } catch (err) {
      console.error("Failed to load plans:", err);
    }
    setShowBuyDialog(true);
  };

  const handleBuyPlan = async (plan) => {
    setBuyingPlan(plan.plan_id);
    try {
      const { ok, data } = await apiCall("POST", `${API}/payments/create-preference`, { plan_id: plan.plan_id });
      if (ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data?.detail || "Error al crear el pago");
      }
    } catch { toast.error("Error de conexion"); }
    finally { setBuyingPlan(null); }
  };

  const handleLogout = async () => {
    try {
      await apiCall("POST", `${API}/auth/logout`);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleNotificationNavigate = (campaignId) => {
    setActiveTab("bulk");
    setFocusCampaignId(campaignId);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--app-bg)" }}>
      <header className="glass fixed top-0 left-0 right-0 z-50 t-border" style={{ borderBottom: "1px solid var(--app-border)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md overflow-hidden">
              <img src="/veritel-logo.jpeg" alt="Veritel" className="w-full h-full" />
            </div>
            <span className="font-black text-xl tracking-tight" style={{ color: "var(--app-text)" }}>Veritel</span>
            {appVersion && <span className="text-xs ml-2" style={{ color: "var(--app-text-muted)" }} data-testid="app-version">v{appVersion}</span>}
            {user?.company_name && (
              <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 ml-2" data-testid="company-badge">
                {user.company_name}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Button variant="ghost" onClick={() => navigate("/admin")} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" data-testid="admin-panel-btn">
                <Settings className="w-4 h-4 mr-2" />Admin
              </Button>
            )}
            <Button variant="ghost" onClick={() => navigate("/history")} className="t-text-secondary hover:bg-violet-500/10">
              <History className="w-4 h-4 mr-2" />Historial
            </Button>
            <Button variant="ghost" onClick={() => navigate("/api-docs")} className="t-text-secondary hover:bg-violet-500/10" data-testid="api-docs-btn">
              <FileText className="w-4 h-4 mr-2" />API
            </Button>
            <NotificationBell onNavigateCampaign={handleNotificationNavigate} />
            <ThemeToggle />
            <div className="flex items-center gap-3 pl-4" style={{ borderLeft: "1px solid var(--app-border)" }}>
              {user?.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />}
              <span className="text-sm hidden sm:block" style={{ color: "var(--app-text-secondary)" }}>{user?.name}</span>
              <ChangePasswordDialog />
              <Button variant="ghost" size="icon" onClick={handleLogout} className="t-text-secondary hover:bg-violet-500/10" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ color: "var(--app-text)" }}>Dashboard</h1>
            <p style={{ color: "var(--app-text-secondary)" }}>
              {isAdmin
                ? "Panel completo - SuperAdmin"
                : `Resultados de ${user?.company_name || "tu empresa"}`
              }
            </p>
          </div>

          <BalanceBar balance={balance} onBuy={handleOpenBuy} />

          {!services ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              {networkError ? (
                <>
                  <p style={{ color: "var(--app-text-secondary)" }}>No se pudo conectar al servidor</p>
                  <Button onClick={loadDashboardData} variant="outline" className="t-text t-border" data-testid="retry-connection-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />Reintentar conexion
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-2" style={{ color: "var(--app-text-muted)" }}>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Cargando...</span>
                </div>
              )}
            </div>
          ) : (
          <Tabs value={activeTab} className="space-y-6" onValueChange={setActiveTab}>
            <TabsList className="p-1" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
              {(services?.phone_validation !== false || services?.email_validation !== false) && (
                <TabsTrigger value="single" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="single-tab">
                  <Phone className="w-4 h-4 mr-2" />Individual
                </TabsTrigger>
              )}
              <TabsTrigger value="bulk" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="bulk-tab">
                <Upload className="w-4 h-4 mr-2" />Campanas
              </TabsTrigger>
              {services?.email_validation !== false && (
                <TabsTrigger value="email-bulk" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="email-bulk-tab">
                  <Mail className="w-4 h-4 mr-2" />Emails
                </TabsTrigger>
              )}
              {isAdmin && (
                <>
                  <TabsTrigger value="api" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="api-tab">
                    <Key className="w-4 h-4 mr-2" />API
                  </TabsTrigger>
                  <TabsTrigger value="enacom" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="enacom-tab">
                    <Database className="w-4 h-4 mr-2" />ENACOM
                  </TabsTrigger>
                </>
              )}
              <TabsTrigger value="usage" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="usage-tab">
                <BarChart3 className="w-4 h-4 mr-2" />Consumo
              </TabsTrigger>
              <TabsTrigger value="api-usage" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="api-usage-tab">
                <Activity className="w-4 h-4 mr-2" />Uso API
              </TabsTrigger>
              {!isAdmin && user?.company_id && (
                <TabsTrigger value="users" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white" style={{ color: "var(--app-text-secondary)" }} data-testid="users-tab">
                  <Users className="w-4 h-4 mr-2" />Usuarios
                </TabsTrigger>
              )}
            </TabsList>

            {(services?.phone_validation !== false || services?.email_validation !== false) && (
              <TabsContent value="single"><SingleTab services={services} /></TabsContent>
            )}
            <TabsContent value="bulk"><BulkTab focusCampaignId={focusCampaignId} onFocusHandled={() => setFocusCampaignId(null)} services={services} isSuperadmin={isAdmin} /></TabsContent>
            {services?.email_validation !== false && (
              <TabsContent value="email-bulk"><EmailBulkTab user={user} /></TabsContent>
            )}
            {isAdmin && (
              <>
                <TabsContent value="api"><ApiTab /></TabsContent>
                <TabsContent value="enacom"><EnacomTab /></TabsContent>
              </>
            )}
            <TabsContent value="usage"><UsageTab /></TabsContent>
            <TabsContent value="api-usage"><ApiUsageTab /></TabsContent>
            {!isAdmin && user?.company_id && (
              <TabsContent value="users"><CompanyUsersTab user={user} /></TabsContent>
            )}
          </Tabs>
          )}
        </div>
      </main>

      {/* Buy Credits Dialog */}
      <AlertDialog open={showBuyDialog} onOpenChange={setShowBuyDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-violet-400" />Comprar Saldo</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Selecciona un plan para cargar saldo. Se te redirigira a MercadoPago.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2">
            {plans.map(plan => {
              const totalIva = Math.round(plan.price * 1.21);
              return (
                <div key={plan.plan_id} className="flex items-center justify-between p-3 bg-zinc-950 border border-zinc-800 rounded-lg hover:border-violet-500/30 transition-colors" data-testid={`buy-plan-${plan.plan_id}`}>
                  <div>
                    <p className="text-white font-semibold text-sm">{plan.name}</p>
                    <p className="text-xs text-zinc-500">Saldo: ${plan.balance.toLocaleString("es-AR")} | Tel ${plan.phone_price} | WA ${plan.wa_price}</p>
                    {plan.discount_pct > 0 && <span className="text-[10px] text-emerald-400">-{plan.discount_pct}% descuento</span>}
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold">${totalIva.toLocaleString("es-AR")}</p>
                    <p className="text-[10px] text-zinc-500">(${plan.price.toLocaleString("es-AR")} + IVA)</p>
                  </div>
                  <Button size="sm" onClick={() => handleBuyPlan(plan)} disabled={buyingPlan === plan.plan_id} className="bg-violet-600 hover:bg-violet-700 text-white ml-3" data-testid={`buy-btn-${plan.plan_id}`}>
                    {buyingPlan === plan.plan_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pagar"}
                  </Button>
                </div>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white">Cerrar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

class DashboardErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Dashboard crash:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
          <div className="text-center space-y-4 max-w-lg p-8">
            <h2 className="text-xl font-bold text-red-400">Error en el Dashboard</h2>
            <p className="text-zinc-400 text-sm">{this.state.error?.message || "Error desconocido"}</p>
            <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }} className="px-4 py-2 bg-violet-600 rounded-lg hover:bg-violet-700">
              Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const DashboardPage = (props) => (
  <DashboardErrorBoundary>
    <Dashboard {...props} />
  </DashboardErrorBoundary>
);

export default DashboardPage;
